/**
 * Logística → Ventas Crédito (`/logistica/ventas-credito`).
 *
 * Puerto de `Credito::ventas/ventas_data` + `views/menu/credito/ventas.php`: las
 * ventas hechas a crédito, con la misma disposición en tarjetas del panel
 * anterior (número, fecha, distintivos de estado, cliente, placa, vendedor y
 * monto) y sus filtros (fechas del mes anterior en adelante, cliente y número).
 *
 * En el panel anterior la pantalla era **sólo para el grupo 1** (administradores),
 * así que la API responde 403 al resto y aquí se muestra ese aviso.
 *
 * Acciones portadas: **Detalles**, **Editar** (placa, conductor, vale y kilometraje) y
 * **Ticket** (PDF que arma el propio panel). Quedan pendientes, con un candado y su
 * motivo, **Anular** (anula además el comprobante en el controlador PTS) y
 * **Permutar** (es otra pantalla).
 */

import {
  ArrowLeft,
  Ban,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Eye,
  Filter,
  Lock,
  Pencil,
  Printer,
  RotateCcw,
  ShieldAlert,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CampoFecha } from '@/components/ui/calendario';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

import {
  DetalleVentaCredito,
  EditarVentaCredito,
} from '../components/venta-credito-dialogos';
import {
  useClientesCredito,
  useEditarVentaCredito,
  useVentaCredito,
  useVentasCredito,
} from '../ventas-credito.api';
import type {
  ClienteCredito,
  VentaCredito,
  VentaCreditoEditable,
  VentasCreditoFiltros,
} from '../ventas-credito.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const POR_PAGINA = 10;

/** Fecha local en `AAAA-MM-DD` (no usar `toISOString`, que cambia de día). */
function fechaLocal(fecha: Date): string {
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');

  return `${fecha.getFullYear()}-${mes}-${dia}`;
}

/**
 * Rango por defecto, como los campos del panel anterior: desde el día 1 del
 * **mes anterior** hasta hoy.
 */
function rangoPorDefecto(): { desde: string; hasta: string } {
  const hoy = new Date();

  return {
    desde: fechaLocal(new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1)),
    hasta: fechaLocal(hoy),
  };
}

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

export function VentasCreditoPage() {
  useDocumentTitle('Logística · Ventas Crédito');

  const inicial = rangoPorDefecto();
  const [desde, setDesde] = useState(inicial.desde);
  const [hasta, setHasta] = useState(inicial.hasta);
  const [numero, setNumero] = useState('');
  const [clienteTexto, setClienteTexto] = useState('');
  const [clienteSel, setClienteSel] = useState<ClienteCredito | null>(null);
  const [filtros, setFiltros] = useState<VentasCreditoFiltros>({
    desde: inicial.desde,
    hasta: inicial.hasta,
    perPage: POR_PAGINA,
    page: 1,
  });
  const [detalleId, setDetalleId] = useState<number | null>(null);
  const [editando, setEditando] = useState(false);
  const [imprimiendo, setImprimiendo] = useState<number | null>(null);
  const [errorImpresion, setErrorImpresion] = useState<string | null>(null);

  const ventas = useVentasCredito(filtros);
  const clientes = useClientesCredito(clienteTexto);
  const detalle = useVentaCredito(detalleId);
  const guardar = useEditarVentaCredito();

  const sinPermiso = ventas.error instanceof ApiError && ventas.error.status === 403;
  const lista = ventas.data?.ventas ?? [];
  const total = ventas.data?.total ?? 0;
  const pagina = ventas.data?.page ?? 1;
  const paginas = ventas.data?.paginas ?? 1;
  const desdeFila = total === 0 ? 0 : (pagina - 1) * POR_PAGINA + 1;
  const hastaFila = Math.min(pagina * POR_PAGINA, total);

  /** Aplica los filtros y vuelve a la primera página. */
  function aplicar() {
    setFiltros({
      desde: desde || undefined,
      hasta: hasta || undefined,
      numero: numero.trim() || undefined,
      clienteId: clienteSel ? String(clienteSel.id) : undefined,
      perPage: POR_PAGINA,
      page: 1,
    });
  }

  /** Abre el ticket de la venta (lo genera la propia API). */
  async function imprimirTicket(venta: VentaCredito) {
    setErrorImpresion(null);
    setImprimiendo(venta.id);

    try {
      await abrirPdfAutenticado(`/api/logistica/ventas-credito/${venta.id}/ticket`);
    } catch (error) {
      setErrorImpresion(mensajeDe(error, 'No se pudo generar el ticket.'));
    } finally {
      setImprimiendo(null);
    }
  }

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/logistica"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Logística
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <CreditCard className="size-6 text-brand-500" aria-hidden="true" />
          Ventas Crédito
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Ventas hechas a crédito con su cliente, su placa, el vendedor que las registró y el estado
          del crédito.
        </p>
      </div>

      {ventas.data ? <Badge variant="outline">{formatNumber(total)} registros</Badge> : null}
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            {mensajeDe(ventas.error, 'Esta pantalla es sólo para administradores.')}
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cabecera}

      <Alert variant="info" title="Acciones en curso de migración">
        Los <strong>detalles</strong>, la <strong>edición</strong> (placa, conductor, vale y
        kilometraje) y el{' '}
        <strong>ticket</strong> ya están disponibles (el ticket lo genera el propio panel).
        El <strong>detalle del comprobante en PTS</strong> y <strong>permutar</strong> son otras
        pantallas del panel anterior, así que se portarán con esos circuitos.
      </Alert>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="vc-desde" className="text-sm font-medium text-ink-700">
                Fecha inicio
              </label>
              <CampoFecha
                id="vc-desde"
               
                className={cn(CONTROL, 'w-40')}
                value={desde}
                onChange={(evento) => setDesde(evento.target.value)}
               tipo="date" />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="vc-hasta" className="text-sm font-medium text-ink-700">
                Fecha fin
              </label>
              <CampoFecha
                id="vc-hasta"
               
                className={cn(CONTROL, 'w-40')}
                value={hasta}
                onChange={(evento) => setHasta(evento.target.value)}
               tipo="date" />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="vc-cliente" className="text-sm font-medium text-ink-700">
                Cliente
              </label>
              <input
                id="vc-cliente"
                className={cn(CONTROL, 'w-72')}
                placeholder="Buscar por nombre o documento"
                value={clienteSel ? clienteSel.nombre : clienteTexto}
                onChange={(evento) => {
                  setClienteSel(null);
                  setClienteTexto(evento.target.value);
                }}
              />
              {!clienteSel && clienteTexto.trim().length >= 2 && (clientes.data?.clientes.length ?? 0) > 0 ? (
                <ul className="max-h-48 w-72 overflow-y-auto rounded-control border border-ink-100 bg-white shadow-card">
                  {(clientes.data?.clientes ?? []).map((opcion) => (
                    <li key={opcion.id}>
                      <button
                        type="button"
                        className="w-full px-3 py-2 text-left text-xs text-ink-700 hover:bg-brand-50"
                        onClick={() => {
                          setClienteSel(opcion);
                          setClienteTexto('');
                        }}
                      >
                        {opcion.nombre}
                        {opcion.documento ? (
                          <span className="ml-1 text-ink-400">{opcion.documento}</span>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="vc-numero" className="text-sm font-medium text-ink-700">
                Número
              </label>
              <input
                id="vc-numero"
                className={cn(CONTROL, 'w-36')}
                placeholder="Buscar…"
                value={numero}
                onChange={(evento) => setNumero(evento.target.value)}
              />
            </div>

            <Button
              size="sm"
              leftIcon={<Filter className="size-3.5" aria-hidden="true" />}
              onClick={aplicar}
            >
              Buscar
            </Button>

            <Button
              variant="outline"
              size="sm"
              leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
              onClick={() => {
                const nuevo = rangoPorDefecto();

                setDesde(nuevo.desde);
                setHasta(nuevo.hasta);
                setNumero('');
                setClienteSel(null);
                setClienteTexto('');
                setFiltros({ desde: nuevo.desde, hasta: nuevo.hasta, perPage: POR_PAGINA, page: 1 });
              }}
            >
              Limpiar
            </Button>
          </div>

          {errorImpresion ? (
            <Alert variant="error" title="No se pudo generar el ticket">
              {errorImpresion}
            </Alert>
          ) : null}

          {ventas.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : ventas.error ? (
            <Alert variant="error" title="No se pudieron leer las ventas a crédito">
              {mensajeDe(ventas.error, 'La API no respondió.')}
            </Alert>
          ) : lista.length === 0 ? (
            <Alert variant="info" title="Sin resultados">
              No se encontraron ventas con esos filtros.
            </Alert>
          ) : (
            <ul className="flex flex-col gap-3">
              {lista.map((venta) => (
                <li
                  key={venta.id}
                  className={cn(
                    'rounded-card border border-ink-100 bg-white p-4 shadow-soft transition-shadow hover:shadow-card',
                    !venta.activo && 'opacity-80',
                  )}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-50 pb-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-ink-900">
                        {venta.serie} - {venta.numero}
                      </span>
                      {venta.tipo_comprobante ? (
                        <Badge variant="outline">{venta.tipo_comprobante}</Badge>
                      ) : null}
                      {venta.editado ? (
                        <Badge variant="warning" title={`Editado el ${venta.actualizado ?? ''}`}>
                          Editado
                        </Badge>
                      ) : null}
                    </div>
                    <span className="text-xs text-ink-400">{venta.creado ?? ''}</span>
                  </div>

                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <div>
                      <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                        Cliente
                      </span>
                      <span className="block text-sm font-semibold text-ink-900">
                        {venta.cliente || '—'}
                      </span>
                      <span className="block text-[11px] text-ink-400">
                        {venta.documento_cliente || 'sin documento'}
                      </span>
                    </div>
                    <div>
                      <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                        Placa
                      </span>
                      <span className="block text-sm text-ink-700">
                        {venta.placa || <span className="text-ink-300">—</span>}
                      </span>
                    </div>
                    <div>
                      <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                        Vendedor
                      </span>
                      <span className="block text-sm text-ink-700">
                        {venta.vendedor || <span className="text-ink-300">—</span>}
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-ink-50 pt-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={venta.activo ? 'success' : 'danger'}>
                        {venta.activo ? 'Activo' : 'Anulado'}
                      </Badge>
                      {venta.canjeado ? <Badge variant="brand">Canjeado</Badge> : null}
                      {venta.estado_sunat ? (
                        <Badge variant="neutral">{venta.estado_sunat}</Badge>
                      ) : null}
                      <span className="text-sm font-semibold text-ink-900">
                        {formatCurrency(venta.total)}
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Ver detalles"
                        aria-label={`Ver los detalles de ${venta.serie}-${venta.numero}`}
                        onClick={() => setDetalleId(venta.id)}
                      >
                        <Eye className="size-4 text-brand-600" aria-hidden="true" />
                      </Button>

                      {venta.puede_editar ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Editar"
                          aria-label={`Editar la venta ${venta.serie}-${venta.numero}`}
                          onClick={() => {
                            guardar.reset();
                            setDetalleId(venta.id);
                            setEditando(true);
                          }}
                        >
                          <Pencil className="size-4 text-amber-600" aria-hidden="true" />
                        </Button>
                      ) : (
                        <Button variant="ghost" size="icon" disabled title="Editar · la venta está anulada">
                          <Lock className="size-4 text-ink-300" aria-hidden="true" />
                        </Button>
                      )}

                      <Button
                        variant="ghost"
                        size="icon"
                        title="Imprimir el ticket de la venta"
                        aria-label={`Imprimir el ticket de la venta ${venta.id}`}
                        loading={imprimiendo === venta.id}
                        onClick={() => void imprimirTicket(venta)}
                      >
                        <Printer className="size-4 text-ink-600" aria-hidden="true" />
                      </Button>

                      {venta.puede_permutar ? (
                        <Button variant="ghost" size="icon" disabled title="Permutar · es otra pantalla">
                          <Lock className="size-4 text-ink-300" aria-hidden="true" />
                        </Button>
                      ) : null}

                      {venta.puede_anular ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled
                          title="Anular · anula además el comprobante en el controlador PTS"
                        >
                          <Lock className="size-4 text-ink-300" aria-hidden="true" />
                        </Button>
                      ) : (
                        <Button variant="ghost" size="icon" disabled title="Anular · la venta está anulada">
                          <Ban className="size-4 text-ink-300" aria-hidden="true" />
                        </Button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {total > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-ink-400">
                Mostrando {formatNumber(desdeFila)}–{formatNumber(hastaFila)} de{' '}
                {formatNumber(total)} registros · página {pagina} de {formatNumber(paginas)}
              </p>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<ChevronLeft className="size-3.5" aria-hidden="true" />}
                  disabled={pagina <= 1}
                  onClick={() => setFiltros({ ...filtros, page: Math.max(1, pagina - 1) })}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  rightIcon={<ChevronRight className="size-3.5" aria-hidden="true" />}
                  disabled={pagina >= paginas}
                  onClick={() => setFiltros({ ...filtros, page: pagina + 1 })}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {detalleId !== null && !editando ? (
        <DetalleVentaCredito
          venta={detalle.data?.venta ?? null}
          cargando={detalle.isLoading}
          error={detalle.error ? mensajeDe(detalle.error, 'No se pudo leer el detalle.') : null}
          onEditar={() => {
            guardar.reset();
            setEditando(true);
          }}
          onCerrar={() => setDetalleId(null)}
        />
      ) : null}

      {detalleId !== null && editando && detalle.data?.venta ? (
        <EditarVentaCredito
          venta={detalle.data.venta}
          guardando={guardar.isPending}
          error={
            guardar.error ? mensajeDe(guardar.error, 'No se pudo guardar el registro.') : null
          }
          onGuardar={(datos: VentaCreditoEditable) =>
            guardar.mutate(
              { id: detalleId, venta: datos },
              {
                onSuccess: () => {
                  setEditando(false);
                  setDetalleId(null);
                },
              },
            )
          }
          onCerrar={() => {
            guardar.reset();
            setEditando(false);
          }}
        />
      ) : null}
    </div>
  );
}
